import React, { useEffect, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import Img from "../ui/Img"
import PriceDisplay from "../ui/PriceDisplay"
import Spinner from "../ui/Spinner"
import { Field, Input, Textarea } from "../account/ui"
import { PlkAvailability, SendFeedback } from "../storeApi"
import { displayName } from "../utils/productUtils"
import { itemUnitPrice, platformId, productCurrency, productPlatforms } from "./cartItems"

const validEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

function Counter({ value, onChange, insufficient, testId }) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={testId}>
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))} className="w-8 h-8 rounded-lg border border-bordered text-[18px] leading-none hover:border-text" aria-label="-">
        −
      </button>
      <span className={`relative min-w-[28px] text-center text-[15px] font-bold ${insufficient ? "text-error" : ""}`} data-testid="platform-quantity">
        {value}
        {insufficient && <span className="absolute -top-1.5 -right-2 w-3.5 h-3.5 rounded-full bg-error text-white text-[10px] font-bold leading-[14px] text-center">!</span>}
      </span>
      <button type="button" onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-lg border border-bordered text-[18px] leading-none hover:border-text" aria-label="+">
        +
      </button>
    </span>
  )
}

/** "Need more licenses?" contact form (user-message {feedback}) */
function ContactForm({ product, onBack }) {
  const t = useT()
  const [form, setForm] = useState({ name: "", email: "", message: t("I'm interested in purchasing more licenses for: %name%", product?.name || "") })
  const [errors, setErrors] = useState({})
  const [state, setState] = useState("idle")
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    const next = {}
    if (form.name.trim().length < 3) next.name = t`Name should be at least 3 characters`
    if (!validEmail(form.email)) next.email = t`The email appears to be invalid`
    if (form.message.trim().length < 10) next.message = t`The message should be at least 10 characters`
    setErrors(next)
    if (Object.keys(next).length) return
    setState("sending")
    try {
      const response = await SendFeedback(`name: ${form.name}\nemail: ${form.email}\nmessage: ${form.message}`, "Product License Request - Aldoo Store")
      setState(response?.result ? "sent" : "failed")
    } catch {
      setState("failed")
    }
  }

  if (state === "sent") {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="license-contact-sent">
        <h4 className="m-0 text-[20px] font-bold">{t`Thank you!`}</h4>
        <p className="m-0 text-text_secondary">{t`We've got your message. We'll get back to you shortly!`}</p>
        <Button variant="secondary" onClick={onBack}>{t`Back`}</Button>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="license-contact-form">
      <h4 className="m-0 text-center text-[20px] font-bold">{t`Contact Us`}</h4>
      <Field label={t`Name`} error={errors.name}>
        <Input name="name" value={form.name} onChange={set("name")} />
      </Field>
      <Field label={t`Email`} error={errors.email}>
        <Input name="email" type="email" value={form.email} onChange={set("email")} />
      </Field>
      <Field label={t`Your Message`} error={errors.message}>
        <Textarea name="message" value={form.message} onChange={set("message")} />
      </Field>
      {state === "failed" && <p className="m-0 text-[14px] text-error">{t`Oops! There was an error sending your message. Please try again.`}</p>}
      <div className="flex gap-3 justify-end">
        <Button type="button" variant="secondary" onClick={onBack}>{t`Back`}</Button>
        <Button type="submit" variant="primary" disabled={state === "sending"}>{t`Submit`}</Button>
      </div>
    </form>
  )
}

/**
 * "SELECT YOUR LICENSE": quantity per supported platform (or one counter when
 * the product's keys are universal), live stock check (product {plkAvailability}),
 * subtotal and the "Need more licenses?" contact form.
 *
 * onChange({ platform, licenses, universal, valid }) fires on every change so a
 * host (dialog / checkout step) can drive its own Continue button; `onConfirm`
 * renders a confirm button inside when given.
 */
export default function PlatformSelector({ product, initialDistribution, initialQuantity, onChange, onConfirm, confirmLabel, compact = false }) {
  const t = useT()
  const platforms = productPlatforms(product)
  const [counts, setCounts] = useState(() => {
    const init = {}
    platforms.forEach((entry, i) => {
      const found = initialDistribution?.find((d) => d.platformId === platformId(entry))
      init[i] = found?.quantity ?? (initialDistribution?.length ? 0 : i === 0 ? 1 : 0)
    })
    return init
  })
  const [universal, setUniversal] = useState(null) // null = detecting, false, or { available }
  const [universalCount, setUniversalCount] = useState(initialQuantity || 1)
  const [availability, setAvailability] = useState(null)
  const [checking, setChecking] = useState(false)
  const [contact, setContact] = useState(false)
  const debounce = useRef(null)

  // Universal keys (no platform) vs per-platform keys
  useEffect(() => {
    let alive = true
    PlkAvailability(product.pid, [{ platformId: null, quantity: 0 }], { allStates: true })
      .then((res) => alive && setUniversal(res?.availability?.[0]?.available > 0 ? { available: res.availability[0].available } : false))
      .catch(() => alive && setUniversal(false))
    return () => {
      alive = false
    }
  }, [product?.pid])

  const distribution = platforms.reduce((arr, entry, i) => {
    const quantity = counts[i] || 0
    const id = platformId(entry)
    if (quantity && id) arr.push({ platformId: id, quantity })
    return arr
  }, [])
  const perPlatformTotal = distribution.reduce((s, d) => s + d.quantity, 0)
  const isUniversal = !!universal
  const licenses = isUniversal ? universalCount : perPlatformTotal

  // Debounced live availability
  useEffect(() => {
    if (universal === null) return
    const query = isUniversal ? (universalCount ? [{ platformId: null, quantity: universalCount }] : []) : distribution
    if (!query.length) {
      setAvailability(null)
      setChecking(false)
      return
    }
    setChecking(true)
    clearTimeout(debounce.current)
    debounce.current = setTimeout(async () => {
      try {
        const res = await PlkAvailability(product.pid, query, { includeReserved: true })
        if (res?.availability) setAvailability(res.availability)
      } catch {
        // keep the previous state
      } finally {
        setChecking(false)
      }
    }, 300)
    return () => clearTimeout(debounce.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(distribution), universalCount, universal])

  const availableFor = (id) => availability?.find((a) => a.platformId === id)
  const insufficient = (id, requested) => {
    const entry = availableFor(id)
    return !!entry && entry.available < requested
  }
  const anyInsufficient = isUniversal ? insufficient(null, universalCount) : distribution.some((d) => insufficient(d.platformId, d.quantity))
  const valid = licenses > 0 && !anyInsufficient && !checking

  useEffect(() => {
    onChange?.({ platform: isUniversal ? [] : distribution, licenses, universal: isUniversal, valid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(distribution), licenses, isUniversal, valid])

  const unit = itemUnitPrice(product)
  const currency = productCurrency(product)
  const img = product?.square || product?.thumb

  if (contact) return <ContactForm product={product} onBack={() => setContact(false)} />

  return (
    <div className="flex flex-col items-center gap-4" data-testid="platform-selector" data-pid={product.pid}>
      {!compact && (
        <div className="flex flex-col items-center gap-2 text-center">
          <Img src={img} alt={product.name} width={120} height={120} className="w-[120px] h-[120px] rounded-lg overflow-hidden" />
          <h3 className="m-0 text-[20px] font-bold leading-tight">{displayName(product)}</h3>
        </div>
      )}

      {universal === null && <Spinner className="py-6" />}

      {universal !== null && isUniversal && (
        <div className="w-full max-w-[360px] flex flex-col items-center gap-3">
          <Counter value={universalCount} onChange={setUniversalCount} insufficient={insufficient(null, universalCount)} testId="platform-row-universal" />
          {insufficient(null, universalCount) && <span className="text-[12px] text-error">{universal.available} {t`licenses in Stock`}</span>}
          <div className="w-full border-t border-bordered pt-3 flex flex-col gap-2">
            {platforms.map((entry, i) => {
              const p = entry.platform || entry
              return (
                <span key={i} className="inline-flex items-center gap-2 px-2 text-[14px] text-text_secondary">
                  {p.image && <img src={p.image} alt={p.name} className="h-5 w-auto object-contain" />}
                  {p.name}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {universal === false && (
        <div className="w-full max-w-[360px] flex flex-col">
          {platforms.map((entry, i) => {
            const p = entry.platform || entry
            const id = platformId(entry)
            const count = counts[i] || 0
            const short = insufficient(id, count)
            return (
              <div key={i} className={`flex items-center gap-3 px-2 py-3 ${i > 0 ? "border-t border-bordered" : ""}`} data-testid="platform-row" data-platform={p.name}>
                <div className="flex-1 min-w-0 flex items-start gap-2">
                  {p.image && <img src={p.image} alt={p.name} className="h-[22px] w-auto object-contain mt-px shrink-0" />}
                  <div className="min-w-0">
                    <div className={`text-[14px] font-medium truncate ${short ? "text-error" : ""}`}>
                      {p.name} {t`License`}
                    </div>
                    {short && (
                      <div className="text-[11px] text-error leading-tight">
                        {availableFor(id).available} {t`licenses for`} {p.name} {t`in Stock`}
                      </div>
                    )}
                  </div>
                </div>
                <Counter value={count} onChange={(v) => setCounts((c) => ({ ...c, [i]: v }))} insufficient={short} />
              </div>
            )
          })}
        </div>
      )}

      <div className="w-full max-w-[360px] flex items-center justify-between border-t border-bordered pt-3">
        <span className="text-text_secondary">{t`Subtotal`}</span>
        <PriceDisplay amount={unit * licenses} currency={currency} variant="small" className="min-w-0" />
      </div>

      {onConfirm && (
        <Button variant="primary_l" className="w-full max-w-[360px]" disabled={!valid} onClick={() => onConfirm({ platform: isUniversal ? [] : distribution, licenses, universal: isUniversal })} data-testid="platform-confirm">
          {checking ? <Spinner size={16} /> : null}
          {confirmLabel ?? t`Continue`}
        </Button>
      )}

      <button type="button" onClick={() => setContact(true)} className="text-[15px] text-primary underline hover:opacity-80" data-testid="need-more-licenses">
        {t`Need more licenses?`}
      </button>
    </div>
  )
}
